import { Modal } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { DocumentViewer } from "@shared/components";
import { buildAssetUrl } from "@shared/utils";

import {
  useInspectionReportQuery,
  type InspectionListItem,
} from "../../api/inspection";

export interface InspectionReportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  structureId: string;
  inspection: InspectionListItem | null;
}

/**
 * Prévia da ficha finalizada (QT-INSP-03-27 / 03-54). Y=9 devolve o caminho
 * relativo no bucket; a URL sai de `buildAssetUrl`. Diálogo travado em 88dvh
 * (sem scroll de fora); o viewer preenche o restante e abre em FitH. Rodapé
 * só com Fechar / Download (sem fluxo de aceite).
 */
const InspectionReportPreviewModal = ({
  isOpen,
  onClose,
  structureId,
  inspection,
}: InspectionReportPreviewModalProps) => {
  const { translate } = useLocalization();

  const { data: report, isLoading } = useInspectionReportQuery(
    isOpen ? structureId : undefined,
    isOpen ? inspection?.id : undefined,
  );

  const fileUrl = report ? buildAssetUrl(report.storagePath) : undefined;
  // FitH: a página cabe na largura do viewer — sem scroll lateral do PDF.
  const viewerUrl = fileUrl ? `${fileUrl}#view=FitH` : undefined;

  const title = inspection
    ? `${translate("inspection.table.pdfPreviewTitle")} · ${translate("inspection.table.columns.code")} ${inspection.inspectionCode}`
    : translate("inspection.table.pdfPreviewTitle");

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={title}
      width="1160px"
      content={
        <div className="insp-pdf-preview">
          <div data-testid="inspection-report-preview" className="insp-pdf-preview-stage">
            {viewerUrl ? (
              <DocumentViewer fileUrl={viewerUrl} fill />
            ) : (
              <div className="insp-pdf-preview-empty">
                {isLoading
                  ? translate("inspection.loading")
                  : translate("inspection.table.pdfUnavailable")}
              </div>
            )}
          </div>

          <div className="insp-pdf-preview-foot">
            {report?.pdfVerificationCode ? (
              <p className="insp-pdf-preview-meta">
                {translate("inspection.table.pdfVerificationCode")}{" "}
                <span className="font-mono">{report.pdfVerificationCode}</span>
              </p>
            ) : (
              <span />
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="insp-btn insp-btn-outline insp-btn-sm"
                onClick={onClose}
              >
                {translate("inspection.actions.close")}
              </button>
              {report && fileUrl ? (
                <a
                  href={fileUrl}
                  download={report.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="insp-btn insp-btn-primary insp-btn-sm"
                >
                  {translate("inspection.actions.download")}
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  title={translate("inspection.table.pdfUnavailable")}
                  className="insp-btn insp-btn-primary insp-btn-sm disabled:opacity-60"
                >
                  {translate("inspection.actions.download")}
                </button>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default InspectionReportPreviewModal;
